<template>
  <fs-modal ref="modalRef" @open="onModalOpen">
    <div class="modal">
      <h2>
        <slot name="title">Delete {{ itemTypeName }}?</slot>
      </h2>

      <p>
        <!-- Asking "are you sure?" reads wrong when the confirm button is inert. -->
        <!-- A caller-specific block explains itself in the usage slot; "used in N places" would not be the reason. -->
        <template v-if="isBlockedByUsage && usageCount && !blockedBy">
          You can not delete the {{ itemTypeName || 'item' }} <strong>{{ itemName }}</strong> because it is used in
          <strong>{{ usageCount }}</strong> place{{ usageCount !== 1 ? 's' : '' }}:
        </template>
        <!-- While the usages are still loading the button is already inert, so asking anything
             would be premature; the usage line below carries the loading state. -->
        <slot v-else-if="!isBlockedByUsage" name="message"
          >Are you sure you want to delete <strong>{{ itemName }}</strong
          >?</slot
        >
      </p>

      <p v-if="fetchUsageCount">
        <template v-if="isLoadingUsage">
          <circular-spinner-full class="modal__spinner" :size="14" color="dark-blue" />
          Loading usage information...
        </template>
        <template v-else>
          <slot name="usage" :usage="usageData" :count="usageCount">
            <!-- Nothing references it, so it is safe to delete -->
            <template v-if="usageCount === 0">
              {{ capitalizeFirstLetter(itemTypeName || 'item') }} is not used anywhere.
            </template>
            <!-- Direct number count -->
            <template v-else-if="typeof usageData === 'number'">
              {{ capitalizeFirstLetter(itemTypeName || 'item') }} is used in <strong>{{ usageData }}</strong> place{{
                usageData !== 1 ? 's' : ''
              }}.
            </template>
            <!-- Array with name property - show detailed list -->
            <template v-else-if="Array.isArray(usageData) && usageData.length > 0 && usageData[0]?.name">
              <div class="modal__usage">
                <p class="modal__usage-warning">
                  This {{ itemTypeName || 'item' }} is used in {{ usageData.length }}
                  {{ usageData.length === 1 ? 'place' : 'places' }}:
                </p>
                <usage-reference-lists :usage="usageData" />
              </div>
            </template>
            <!-- Array without name property - show count only -->
            <template v-else-if="Array.isArray(usageData)">
              {{ capitalizeFirstLetter(itemTypeName || 'item') }} is used in
              <strong>{{ usageData.length }}</strong> place{{ usageData.length !== 1 ? 's' : '' }}.
            </template>
            <!-- Categorized object (either counts or arrays) -->
            <template v-else-if="usageData && typeof usageData === 'object'">
              <template v-if="!isBlockedByUsage">
                {{ capitalizeFirstLetter(itemTypeName || 'item') }} is used
                <fs-tooltip
                  show-even-if-not-ellipsed
                  :text="
                    Object.entries(usageData)
                      .filter(
                        ([key, value]) =>
                          (typeof value === 'number' && value > 0) || (Array.isArray(value) && value.length > 0)
                      )
                      .map(([key, value]) => `${formatCategory(key)}: ${Array.isArray(value) ? value.length : value}`)
                      .join(', ')
                  "
                >
                  <strong>{{ usageCount }}</strong>
                </fs-tooltip>
                time{{ usageCount !== 1 ? 's' : '' }}.
              </template>
              <usage-reference-lists
                v-if="Object.values(usageData).some(isUsageReferenceList)"
                :usage="usageData as Record<string, unknown>"
              />
            </template>
          </slot>
        </template>
      </p>

      <p v-if="additionalWarning" class="modal__warning">
        {{ additionalWarning }}
      </p>

      <button-pair
        class="modal__buttons"
        :action-btn-name="confirmText"
        :action-btn-icon="trashCanIcon"
        :is-loading="isDeleting"
        :is-disabled="isBlockedByUsage"
        @action="handleAction"
        @cancel="handleCancel"
      />
    </div>
  </fs-modal>
</template>

<script setup lang="ts" generic="TUsage extends number | any[] | Record<string, any>">
import { ref, computed } from 'vue';
import FsModal from '@components/modals/fs-modal.vue';
import ButtonPair from '@components/button-pair.vue';
import CircularSpinnerFull from '@components/circular-spinner-full.vue';
import FsTooltip from '@components/poppers/fs-tooltip.vue';
import UsageReferenceLists from '@components/modals/usage-reference-lists.vue';
import { formatUsageCategory as formatCategory, isUsageReferenceList } from '@lib/usage-references';
import trashCanIcon from '@icons/other/trash-can-icon.svg';

const {
  itemName = 'this',
  itemTypeName = 'item',
  confirmText = 'Delete',
  additionalWarning,
  fetchUsageCount,
  deleteCallback,
  blockWhenUsed = false,
  blockedBy,
} = defineProps<{
  // Item information for display
  itemName?: string;
  itemTypeName?: string;

  // Usage stats (optional)
  fetchUsageCount?: () => Promise<TUsage>;

  // Delete action (optional - if not provided, just emits confirm)
  deleteCallback?: () => Promise<void>;

  // Additional content
  additionalWarning?: string;
  confirmText?: string;

  /**
   * Refuse the deletion outright while anything still references the item, rather than letting the
   * server reject it. Opt-in: for several callers a usage is only informational and the deletion
   * goes through regardless.
   */
  blockWhenUsed?: boolean;

  /**
   * Refuse the deletion while this holds for the loaded usages — for callers where only some usages
   * stand in the way and the rest are informational. The caller's usage slot says why.
   */
  blockedBy?: (usage: TUsage) => boolean;
}>();

const emit = defineEmits<{
  confirm: [];
  cancel: [];
  deleted: [];
  closed: [];
}>();

// Modal state
const modalRef = ref<InstanceType<typeof FsModal> | null>(null);
const isDeleting = ref(false);

function open() {
  modalRef.value?.open();
}

function close() {
  modalRef.value?.close();
  emit('closed');
}

// Usage tracking
const isLoadingUsage = ref(false);
const usageData = ref<TUsage | null>(null);

const usageCount = computed(() => {
  if (usageData.value === null || isLoadingUsage.value) return null;

  if (typeof usageData.value === 'number') {
    return usageData.value;
  }

  if (Array.isArray(usageData.value)) {
    return usageData.value.length;
  }

  // For objects, sum all numeric values or array lengths
  if (typeof usageData.value === 'object') {
    return Object.values(usageData.value)
      .map((val) => (typeof val === 'number' ? val : Array.isArray(val) ? val.length : 0))
      .reduce((sum, val) => sum + val, 0);
  }

  return null;
});

// Also covers the window before the usages have loaded, so the button cannot be hit while the
// answer is still unknown.
const isBlockedByUsage = computed(() => {
  if (!fetchUsageCount || (!blockWhenUsed && !blockedBy)) return false;
  if (usageData.value === null || isLoadingUsage.value) return true;
  return blockedBy ? blockedBy(usageData.value) : usageCount.value !== 0;
});

// Action handlers
async function handleAction() {
  emit('confirm');

  if (deleteCallback) {
    isDeleting.value = true;
    try {
      await deleteCallback();
      emit('deleted');
    } finally {
      isDeleting.value = false;
    }
  }

  close();
}

function handleCancel() {
  emit('cancel');
  close();
}

// Enter-to-confirm comes from the button-pair's primary action on the modal's
// shortcut layer, disabled while a deletion is in flight.

// Modal lifecycle
async function onModalOpen() {
  // Fetch usage count when modal opens
  if (fetchUsageCount) {
    isLoadingUsage.value = true;
    usageData.value = null;

    try {
      usageData.value = await fetchUsageCount();
    } catch (error) {
      usageData.value = null;
    } finally {
      isLoadingUsage.value = false;
    }
  }
}

// Utility
function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

defineExpose({ open, close });
</script>

<style scoped lang="scss">
@import '@styles/variables';
@import '@styles/text';

.modal {
  padding: 2.4rem;
  min-width: 50rem;

  h2 {
    @include popper-title;
    margin: 0 0 2rem 0;
  }

  p {
    margin: 0 0 1.6rem 0;
    color: $c-dark-light;
    line-height: 1.5;

    strong {
      color: $c-dark;
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  &__spinner {
    display: inline-block;
    margin: 0 0.5rem;
  }

  &__caption {
    color: rgba($c-dark, 0.6);
    font-size: 1.2rem;
  }

  &__warning {
    color: $c-error;
    font-size: 1.3rem;
    font-style: italic;
    margin-top: 1.6rem !important;
  }

  &__usage {
    margin-top: 1.6rem;
  }

  &__usage-warning {
    margin: 0 0 1.2rem 0;
    font-size: 1.4rem;
    color: $c-dark;
    font-weight: 500;
  }

  &__buttons {
    margin-top: 3rem;
  }
}
</style>
